import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronRight } from 'lucide-react';
import Fuse from 'fuse.js';
import { getAvailableDomains } from '@/domain-registry';
import { buildSearchIndex } from '@/shared/components/search/searchIndex';
import type { SearchEntry } from '@/shared/components/search/searchIndex';
import { Z_INDEX } from '@/lib/z-index';

export interface SearchPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SearchPalette: React.FC<SearchPaletteProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const domains = useMemo(() => getAvailableDomains(), []);
  const searchIndex = useMemo(() => buildSearchIndex(domains), [domains]);

  const fuse = useMemo(
    () =>
      new Fuse(searchIndex, {
        keys: [
          { name: 'label', weight: 2 },
          { name: 'description', weight: 1 },
        ],
        threshold: 0.3,
        ignoreLocation: true,
        includeMatches: true,
      }),
    [searchIndex]
  );

  const results = useMemo<SearchEntry[]>(() => {
    if (!query.trim()) return [];
    return fuse.search(query, { limit: 8 }).map((r) => r.item);
  }, [fuse, query]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  useEffect(() => {
    if (resultsRef.current && results.length > 0) {
      const selected = resultsRef.current.children[selectedIndex] as HTMLElement;
      selected?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, results.length]);

  // Auto-focus input when palette opens; reset query on close
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      // Defer focus so the element is visible first
      setTimeout(() => {
        inputRef.current?.focus();
      }, 10);
    }
  }, [isOpen]);

  const handleSelect = (entry: SearchEntry) => {
    navigate(entry.path);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  if (!isOpen) return null;

  const hasQuery = query.trim().length > 0;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50"
        style={{ zIndex: Z_INDEX.MODAL_BACKDROP }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        className="fixed inset-0 flex items-start justify-center pt-[15vh] px-4"
        style={{ zIndex: Z_INDEX.MODAL_CONTENT }}
        role="dialog"
        aria-modal="true"
        aria-label="Search"
      >
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-200">
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
            <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search pages and domains..."
              className="flex-1 text-sm text-gray-900 placeholder-gray-400 outline-none bg-transparent"
              aria-label="Search query"
              aria-autocomplete="list"
            />
            {!hasQuery && (
              <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-[11px] text-gray-400 font-mono">
                esc
              </kbd>
            )}
          </div>

          {/* Results area */}
          {hasQuery ? (
            <>
              {results.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-400">
                  No results for &ldquo;{query}&rdquo;
                </div>
              ) : (
                <div ref={resultsRef} className="max-h-80 overflow-y-auto">
                  {results.map((entry, index) => {
                    const Icon = entry.domainIcon;
                    const isSelected = index === selectedIndex;
                    return (
                      <button
                        key={entry.path}
                        onMouseDown={() => handleSelect(entry)}
                        onMouseEnter={() => setSelectedIndex(index)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                          isSelected ? 'bg-gray-50' : 'hover:bg-gray-50'
                        }`}
                        aria-selected={isSelected}
                      >
                        <Icon
                          className={`w-4 h-4 flex-shrink-0 ${
                            isSelected ? 'text-[#5b3d72]' : 'text-gray-400'
                          }`}
                        />
                        <div className="min-w-0 flex-1">
                          <span
                            className={`text-sm block truncate ${
                              isSelected ? 'text-gray-900 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {entry.label}
                          </span>
                          {entry.description && (
                            <span className="text-xs text-gray-400 block truncate mt-0.5">
                              {entry.description}
                            </span>
                          )}
                        </div>
                        <ChevronRight className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="px-4 py-6 text-center text-sm text-gray-400">
              Start typing to search pages and domains
            </div>
          )}

          {/* Footer with keyboard hints */}
          <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-4 text-[11px] text-gray-400">
            <span>
              <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[10px]">&#8593;&#8595;</kbd>{' '}
              Navigate
            </span>
            <span>
              <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[10px]">&#9166;</kbd> Go
            </span>
            <span>
              <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[10px]">esc</kbd> Close
            </span>
          </div>
        </div>
      </div>
    </>
  );
};
